import React, { useEffect, useRef, useState } from "react";
import * as d3 from "d3";

/**
 * Pipeline Build Bar Chart Component
 *
 * Displays a vertical bar chart showing Qualified Pipeline build ($M) by month
 * Includes a reference line at the target value (default 20 $M)
 */
function PipelineBuildBarChart({ data, title, targetLine = 20, onDrill, source }) {
  const chartRef = useRef(null);
  const [containerWidth, setContainerWidth] = useState(0);

  // Handle resize
  useEffect(() => {
    if (!chartRef.current) return;

    const resizeObserver = new ResizeObserver((entries) => {
      for (let entry of entries) {
        const width = entry.contentRect.width;
        if (width > 0) {
          setContainerWidth(width);
        }
      }
    });

    resizeObserver.observe(chartRef.current);
    return () => resizeObserver.disconnect();
  }, []);

  useEffect(() => {
    if (
      !data ||
      !chartRef.current ||
      !Array.isArray(data) ||
      data.length === 0 ||
      containerWidth === 0
    ) {
      return;
    }

    d3.select(chartRef.current).selectAll("*").remove();

    // Responsive sizing
    const isSmallScreen = containerWidth < 400;
    const isMediumScreen = containerWidth < 600;

    const margin = {
      top: isSmallScreen ? 40 : 50,
      right: isSmallScreen ? 15 : 30,
      bottom: isSmallScreen ? 50 : 60,
      left: isSmallScreen ? 50 : 70
    };
    const width = Math.max(200, containerWidth - margin.left - margin.right);
    const height = isSmallScreen ? 280 : 350;

    // Responsive font sizes
    const fontSize = {
      title: isSmallScreen ? 11 : (isMediumScreen ? 12 : 14),
      axis: isSmallScreen ? 8 : (isMediumScreen ? 9 : 10),
      label: isSmallScreen ? 9 : (isMediumScreen ? 10 : 11),
      barLabel: isSmallScreen ? 9 : (isMediumScreen ? 10 : 11)
    };

    const totalWidth = width + margin.left + margin.right;
    const totalHeight = height + margin.top + margin.bottom;

    const svg = d3
      .select(chartRef.current)
      .append("svg")
      .attr("viewBox", `0 0 ${totalWidth} ${totalHeight}`)
      .attr("preserveAspectRatio", "xMidYMid meet")
      .style("width", "100%")
      .style("height", "auto")
      .style("max-height", isSmallScreen ? "350px" : "450px")
      .style("display", "block")
      .append("g")
      .attr("transform", `translate(${margin.left},${margin.top})`);

    const monthDomain = data.map((d) => d.yearMonth).filter(Boolean);
    const xScale = d3
      .scaleBand()
      .domain(monthDomain)
      .range([0, width])
      .padding(isSmallScreen ? 0.3 : 0.4);

    // Y Scale - values in millions
    const maxDataValue = d3.max(data, (d) => d.value || 0) || 0;
    const maxValue = Math.max(
      targetLine && targetLine > 0 ? targetLine * 1.2 : 0,
      maxDataValue * 1.1 || 30,
    );
    const yScale = d3.scaleLinear().domain([0, maxValue]).range([height, 0]);

    // Grid lines
    svg
      .append("g")
      .attr("class", "grid")
      .call(d3.axisLeft(yScale).ticks(6).tickSize(-width).tickFormat(""))
      .selectAll("line")
      .style("stroke", "#e0e0e0")
      .style("stroke-opacity", 0.7);

    svg.selectAll(".domain").remove();

    // X Axis
    // svg
    //   .append("g")
    //   .attr("transform", `translate(0,${height})`)
    //   .call(
    //     d3
    //       .axisBottom(xScale)
    //       .tickSize(0)
    //       .tickFormat((d) => {
    //         const item = data.find((it) => it.yearMonth === d);
    //         return item ? item.yearMonth : d;
    //       }),
    //   )
    //   .selectAll("text")
    //   .style("font-size", `${fontSize.axis}px`)
    //   .style("text-anchor", "middle");

    // X Axis - with rotated labels
svg
  .append("g")
  .attr("transform", `translate(0,${height})`)
  .call(
    d3
      .axisBottom(xScale)
      .tickSize(0)
      .tickFormat((d) => {
        const item = data.find((it) => it.yearMonth === d);
        return item ? item.yearMonth : d;
      }),
  )
  .selectAll("text")
  .style("font-size", `${fontSize.axis}px`)
  .style("text-anchor", "end")  // Changed from "middle"
  .attr("dx", "-0.5em")          // Added: shift left
  .attr("dy", "0.15em")          // Added: shift down slightly
  .attr("transform", "rotate(-45)"); // Added: rotate labels

    // Y Axis
    svg
      .append("g")
      .call(
        d3
          .axisLeft(yScale)
          .ticks(isSmallScreen ? 4 : 6)
          .tickFormat((d) => `${d}`),
      )
      .selectAll("text")
      .style("font-size", `${fontSize.axis}px`);

    // Draw bars
    svg
      .selectAll(".bar")
      .data(data)
      .enter()
      .append("rect")
      .attr("class", "bar")
      .attr("x", (d) => {
        const xPos = xScale(d.yearMonth);
        if (xPos === undefined) {
          console.warn(
            `PipelineBuildBarChart: xScale returned undefined for key "${d.yearMonth}"`,
            { monthDomain, dataKey: d.yearMonth },
          );
        }
        return xPos !== undefined ? xPos : 0;
      })
      .attr("y", (d) => {
        const value = Number(d.value) || 0;
        return yScale(value);
      })
      .attr("width", xScale.bandwidth())
      .attr("height", (d) => {
        const value = Number(d.value) || 0;
        const barHeight = height - yScale(value);
        return Math.max(barHeight, 0);
      })
      .attr("fill", "#4472C4")
      .attr("rx", 2)
      .attr("stroke", "#fff")
      .attr("stroke-width", 0.5)
      .style("cursor", onDrill ? "pointer" : "default")
      .on("click", (d) => {
        if (onDrill && d.value > 0) {
          onDrill({ source, yearMonth: d.yearMonth });
        }
      });

    // Value labels on bars
    svg
      .selectAll(".bar-label")
      .data(data.filter((d) => d.value > 0))
      .enter()
      .append("text")
      .attr("class", "bar-label")
      .attr("x", (d) => xScale(d.yearMonth) + xScale.bandwidth() / 2)
      .attr("y", (d) => yScale(d.value) - 5)
      .attr("text-anchor", "middle")
      .attr("font-size", `${fontSize.barLabel}px`)
      .attr("font-weight", "bold")
      .attr("fill", "#333")
      .text((d) => d.valueFormatted || d.value.toFixed(1));

    // Target/reference line (red horizontal line)
    if (targetLine && targetLine > 0) {
      svg
        .append("line")
        .attr("x1", 0)
        .attr("x2", width)
        .attr("y1", yScale(targetLine))
        .attr("y2", yScale(targetLine))
        .attr("stroke", "#d32f2f")
        .attr("stroke-width", 2)
        .attr("stroke-dasharray", "5,5");

      // Target label
      svg
        .append("text")
        .attr("x", width - 5)
        .attr("y", yScale(targetLine) - 5)
        .attr("text-anchor", "end")
        .attr("font-size", `${fontSize.axis}px`)
        .attr("font-weight", "bold")
        .attr("fill", "#d32f2f")
        .text(`${targetLine}`);
    }

    // Title
    svg
      .append("text")
      .attr("x", width / 2)
      .attr("y", isSmallScreen ? -20 : -25)
      .attr("text-anchor", "middle")
      .attr("font-size", `${fontSize.title}px`)
      .attr("font-weight", "bold")
      .text(title || "");

    // Y axis label
    svg
      .append("text")
      .attr("transform", "rotate(-90)")
      .attr("x", -height / 2)
      .attr("y", isSmallScreen ? -35 : -50)
      .attr("text-anchor", "middle")
      .attr("font-size", `${fontSize.label}px`)
      .text("Pipeline build ($M)");
  }, [data, title, targetLine, containerWidth, onDrill, source]);

  return (
    <div
      ref={chartRef}
      id="printChart"
      style={{
        width: "100%",
        minHeight: "300px",
        display: "flex",
        justifyContent: "center",
        alignItems: "center",
        overflow: "hidden",
      }}
    />
  );
}

export default PipelineBuildBarChart;
